import { transcribeAudio } from '../../utils/transcribe';
import type { STTConfig, STTError, STTResult } from './types';

/** Result of attempting a backend transcription. */
export type TranscribeOutcome =
  | { ok: true; result: STTResult }
  | { ok: false; error: STTError };

/** Signature of the transcription transport (matches `transcribeAudio`). */
export type TranscribeFn = typeof transcribeAudio;

function classifyError(err: unknown): STTError {
  // getUserMedia / fetch network failures surface as TypeError or carry
  // network-ish messages; everything else is a server/engine error.
  const message = err instanceof Error ? err.message : String(err);
  if (err instanceof TypeError || /network|failed to fetch/i.test(message)) {
    return { code: 'network', message };
  }
  return { code: 'unknown', message };
}

/**
 * POST an audio blob to the backend (GCP) transcription endpoint and classify
 * the outcome into the normalized {@link STTResult} / {@link STTError} shape.
 *
 * Shared by {@link BackendSTTProvider} and the native-mode fallback, so error
 * classification lives in exactly one place.
 */
export async function transcribeBlob(
  blob: Blob,
  config: STTConfig,
  transcribe: TranscribeFn = transcribeAudio
): Promise<TranscribeOutcome> {
  try {
    const response = await transcribe(
      blob,
      config.languageCode,
      undefined,
      config.phraseHints ?? undefined,
      { runId: config.runId, stepIndex: config.stepIndex }
    );
    if (response.transcript.trim().length === 0) {
      return {
        ok: false,
        error: {
          code: 'no-speech',
          message: "We didn't catch that — try again",
        },
      };
    }
    return {
      ok: true,
      result: {
        transcript: response.transcript,
        confidence: response.confidence,
        isFinal: true,
        ...(response.words ? { words: response.words } : {}),
      },
    };
  } catch (err) {
    return { ok: false, error: classifyError(err) };
  }
}
